// SF22.2: the actual retarget algorithm - the hard, error-prone part of
// custom rigging. A canonical clip's per-bone tracks are authored against
// the shared rig's own rest pose; playing them unmodified on an imported
// rig with a *different* rest pose (different bind orientations per bone)
// produces the wrong result, because a bone-local rotation/translation only
// makes sense relative to the rig it was authored for. This module strips
// each keyframe down to "how far has this bone moved from its own rest
// pose" (a bind-pose-relative delta) and reapplies that same delta on top
// of the source rig's own rest pose - the standard retarget technique.
//
// Known limitation: this assumes the source and canonical rigs' bone-local
// axis conventions agree (both rigs use the same "which way is forward for
// this bone" authoring convention). It does not attempt cross-axis
// remapping (e.g. a rig authored with a different up-axis per bone). Get a
// visibly wrong retarget on an unusual rig? That axis-convention mismatch is
// the likely cause, and is a real follow-up, not a bug in the delta maths
// itself (which is covered by hand-computed unit tests below).
//
// Kept deliberately identical to its sibling copy at
// sunder/sstk-sunder-web/src/prototype/src/retarget.ts, needed there so the
// built-in UAL2 rig option (ual2Retarget.ts) can retarget the canonical
// clips at runtime without a dependency on this authoring-only package. See
// loader.ts's own header for why this duplication (rather than a shared
// runtime package) is the current answer - the same reasoning applies here.
import * as THREE from 'three';

import type { ImportedBone } from './importRig';

/** A bone's rest-pose local transform, as THREE objects ready for maths
 * (ImportedBone stores plain arrays; this is the same data, just typed). */
export interface RestPose {
  [boneName: string]: {
    position: THREE.Vector3;
    quaternion: THREE.Quaternion;
  };
}

/** Build a RestPose lookup from a walked skeleton (SF22.1's ImportedBone[]). */
export function buildRestPose(bones: readonly ImportedBone[]): RestPose {
  const pose: RestPose = {};
  for (const bone of bones) {
    const [px, py, pz] = bone.restTransform.position;
    const [qx, qy, qz, qw] = bone.restTransform.quaternion;
    pose[bone.name] = {
      position: new THREE.Vector3(px, py, pz),
      quaternion: new THREE.Quaternion(qx, qy, qz, qw),
    };
  }
  return pose;
}

/**
 * Retarget one bone's animated local quaternion from the canonical rig onto
 * the source rig. `canonicalBind^-1 * canonicalAnim` is the animated pose's
 * rotation *relative to the canonical bone's own rest* (in the canonical
 * bone's own local frame) - the actual joint articulation, independent of
 * how either rig's rest pose happens to be authored. Reapplying that delta
 * on top of the source bone's own rest pose carries the same articulation
 * onto a rig with a different rest orientation.
 */
export function retargetBoneQuaternion(
  sourceBind: THREE.Quaternion,
  canonicalBind: THREE.Quaternion,
  canonicalAnim: THREE.Quaternion,
): THREE.Quaternion {
  const delta = canonicalBind.clone().invert().multiply(canonicalAnim);
  return sourceBind.clone().multiply(delta);
}

/**
 * Retarget one bone's animated local position the same way, additively:
 * the displacement from the canonical bone's own rest position is added on
 * top of the source bone's own rest position. No scale correction between
 * rigs of different proportions is applied - a known limitation for rigs
 * with substantially different limb lengths, noted rather than solved here.
 */
export function retargetBonePosition(
  sourceBind: THREE.Vector3,
  canonicalBind: THREE.Vector3,
  canonicalAnim: THREE.Vector3,
): THREE.Vector3 {
  return sourceBind.clone().add(canonicalAnim.clone().sub(canonicalBind));
}

/** Canonical joint name -> source joint name. Canonical joints absent as a
 * key are treated as unmapped (their tracks are dropped by retargetClip,
 * not guessed at). */
export type BoneMapping = Record<string, string>;

export interface RetargetResult {
  clip: THREE.AnimationClip;
  /** Canonical joints the clip animates that had no mapping entry (or whose
   * mapped source joint has no rest-pose entry) - their tracks were
   * dropped. Surfaced so the bone-mapping UI can warn "N joints unmapped". */
  unmappedBones: string[];
}

/**
 * Retarget every track of a canonical AnimationClip onto the source rig's
 * bones, per `mapping`. Keyframe times are untouched - only the per-keyframe
 * quaternion/position values and the track's bone name change - so this is
 * a value transform, not a resample.
 */
export function retargetClip(
  clip: THREE.AnimationClip,
  mapping: BoneMapping,
  canonicalRest: RestPose,
  sourceRest: RestPose,
): RetargetResult {
  const tracks: THREE.KeyframeTrack[] = [];
  const unmapped = new Set<string>();

  for (const track of clip.tracks) {
    const { nodeName, propertyName } = THREE.PropertyBinding.parseTrackName(track.name);
    const sourceName = nodeName ? mapping[nodeName] : undefined;
    const canonicalBindEntry = nodeName ? canonicalRest[nodeName] : undefined;
    const sourceBindEntry = sourceName ? sourceRest[sourceName] : undefined;

    if (!nodeName || !sourceName || !canonicalBindEntry || !sourceBindEntry) {
      if (nodeName) unmapped.add(nodeName);
      continue;
    }

    if (propertyName === 'quaternion') {
      const values = new Float32Array(track.values.length);
      const anim = new THREE.Quaternion();
      for (let i = 0; i < track.values.length; i += 4) {
        anim.set(track.values[i]!, track.values[i + 1]!, track.values[i + 2]!, track.values[i + 3]!);
        const retargeted = retargetBoneQuaternion(sourceBindEntry.quaternion, canonicalBindEntry.quaternion, anim);
        values[i] = retargeted.x;
        values[i + 1] = retargeted.y;
        values[i + 2] = retargeted.z;
        values[i + 3] = retargeted.w;
      }
      tracks.push(new THREE.QuaternionKeyframeTrack(`${sourceName}.quaternion`, Array.from(track.times), Array.from(values)));
      continue;
    }

    if (propertyName === 'position') {
      const values = new Float32Array(track.values.length);
      const anim = new THREE.Vector3();
      for (let i = 0; i < track.values.length; i += 3) {
        anim.set(track.values[i]!, track.values[i + 1]!, track.values[i + 2]!);
        const retargeted = retargetBonePosition(sourceBindEntry.position, canonicalBindEntry.position, anim);
        values[i] = retargeted.x;
        values[i + 1] = retargeted.y;
        values[i + 2] = retargeted.z;
      }
      tracks.push(new THREE.VectorKeyframeTrack(`${sourceName}.position`, Array.from(track.times), Array.from(values)));
      continue;
    }

    // Scale (and anything else) passes through unmodified onto the mapped
    // bone name - this rig has no animated scale tracks today, so this is
    // a defensive fallback, not a load-bearing path.
    const clonedTrack = track.clone();
    clonedTrack.name = `${sourceName}.${propertyName}`;
    tracks.push(clonedTrack);
  }

  return { clip: new THREE.AnimationClip(clip.name, clip.duration, tracks), unmappedBones: [...unmapped] };
}

// ── Name-similarity auto-suggest ────────────────────────────────────────

type Side = 'l' | 'r' | null;

const RIG_PREFIX_RE = /^(mixamorig:?|def-)/i;

/** Split a joint name into lowercase word tokens: strips common rig-prefix
 * noise (mixamorig:, DEF-), then splits on camelCase boundaries and
 * ._- separators. */
function tokenize(raw: string): string[] {
  const spaced = raw
    .replace(RIG_PREFIX_RE, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[._-]+/g, ' ');
  return spaced.toLowerCase().split(/\s+/).filter(Boolean);
}

/** Pull a left/right side marker off a token list (a leading/trailing
 * "left"/"right" word, or Blender's bare trailing "l"/"r"). */
function extractSide(tokens: string[]): { tokens: string[]; side: Side } {
  const sideOf = (w: string): Side => (w === 'left' || w === 'l' ? 'l' : w === 'right' || w === 'r' ? 'r' : null);
  if (tokens.length > 1) {
    const first = sideOf(tokens[0]!);
    if (first) return { tokens: tokens.slice(1), side: first };
    const last = sideOf(tokens[tokens.length - 1]!);
    if (last) return { tokens: tokens.slice(0, -1), side: last };
  }
  return { tokens, side: null };
}

/** Known synonym segments for common exporters (Mixamo especially) whose
 * words share no substring with this rig's Blender-rigify-deform naming.
 * Not exhaustive - manual override in the bone-mapping UI is the actual
 * correctness guarantee, this only improves the suggested starting point.
 *
 * The `pelvis`..`ring03` entries below cover Quaternius's UE-style "Universal
 * Animation Library 2" naming (`pelvis`, `spine_01..03`, `clavicle_l/r`,
 * `upperarm_l/r`, `lowerarm_l/r`, `calf_l/r`, `ball_l/r`, `index_01_l/r`, etc,
 * see UAL2_Standard.glb / Mannequin_F.glb). Two UAL2 quirks worth noting for a
 * future rig sharing this convention: its `neck_01`/`spine_01..03` numbering
 * has no canonical equivalent for `neck` (the canonical rig has a single,
 * unnumbered neck joint) so `neck01` maps straight to `neck`; and its finger/
 * spine segments are zero-padded to 2 digits (`01`) against the canonical
 * rig's 2-digit finger joints but 3-digit spine joints (`DEF-spine001`), so
 * both widths need their own entry rather than one shared numeric rule. */
const SEGMENT_SYNONYMS: Record<string, string> = {
  arm: 'upperarm',
  upleg: 'thigh',
  leg: 'shin',
  toebase: 'toe',
  handindex: 'findex',
  handmiddle: 'fmiddle',
  handpinky: 'fpinky',
  handring: 'fring',
  handthumb: 'thumb',
  // UAL2 (Universal Animation Library 2 / UE mannequin naming)
  pelvis: 'hips',
  spine01: 'spine001',
  spine02: 'spine002',
  spine03: 'spine003',
  neck01: 'neck',
  clavicle: 'shoulder',
  lowerarm: 'forearm',
  calf: 'shin',
  ball: 'toe',
  index01: 'findex01',
  index02: 'findex02',
  index03: 'findex03',
  middle01: 'fmiddle01',
  middle02: 'fmiddle02',
  middle03: 'fmiddle03',
  pinky01: 'fpinky01',
  pinky02: 'fpinky02',
  pinky03: 'fpinky03',
  ring01: 'fring01',
  ring02: 'fring02',
  ring03: 'fring03',
};

function normalizedKey(name: string): { side: Side; key: string } {
  const { tokens, side } = extractSide(tokenize(name));
  const joined = tokens.join('');
  return { side, key: SEGMENT_SYNONYMS[joined] ?? joined };
}

/**
 * Suggest a canonical -> source bone mapping by name similarity, matching
 * the worked example this task is scoped around: Mixamo `mixamorig:LeftHand`
 * suggests `DEF-handL`. Only exact (side, normalised-key) matches suggest a
 * mapping; anything it can't confidently resolve is left unmapped for the
 * user to fill in manually rather than guessed at.
 */
export function suggestBoneMapping(canonicalNames: readonly string[], sourceNames: readonly string[]): BoneMapping {
  const bySourceKey = new Map<string, string>();
  for (const name of sourceNames) {
    const { side, key } = normalizedKey(name);
    bySourceKey.set(`${side ?? ''}:${key}`, name);
  }

  const mapping: BoneMapping = {};
  for (const name of canonicalNames) {
    const { side, key } = normalizedKey(name);
    const match = bySourceKey.get(`${side ?? ''}:${key}`);
    if (match) mapping[name] = match;
  }
  return mapping;
}
